import React, { useState, useRef } from 'react';
import { Upload, File, X, AlertCircle } from 'lucide-react';
import { parseExcelFile } from '../lib/emailImportHelpers';

interface FileUploadZoneProps {
  onFileRead: (content: string, fileName: string) => void;
  disabled?: boolean;
}

export function FileUploadZone({ onFileRead, disabled = false }: FileUploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = async (file: File) => {
    setError('');
    setSelectedFile(file);

    const acceptedTypes = [
      'text/csv',
      'text/plain',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    const acceptedExtensions = ['.csv', '.txt', '.xlsx', '.xls'];
    const hasValidExtension = acceptedExtensions.some(ext =>
      file.name.toLowerCase().endsWith(ext)
    );

    if (!acceptedTypes.includes(file.type) && !hasValidExtension) {
      setError('Alleen CSV, TXT, en Excel bestanden zijn toegestaan');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Bestand is te groot (max 5MB)');
      return;
    }

    try {
      let text: string;

      if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
        text = await parseExcelFile(file);
      } else {
        text = await readFileAsText(file);
      }

      onFileRead(text, file.name);
    } catch (err: any) {
      setError(err.message || 'Kon bestand niet lezen. Probeer het opnieuw.');
      console.error('File read error:', err);
    }
  };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Kon bestand niet lezen'));
      reader.readAsText(file);
    });
  };

  const clearFile = () => {
    setSelectedFile(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging
            ? 'border-[#FF6B35] bg-orange-50'
            : disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : 'border-gray-300 hover:border-[#FF6B35] cursor-pointer'
        }`}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt,.xlsx,.xls"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />

        {selectedFile ? (
          <div className="flex items-center justify-center gap-3">
            <File className="w-8 h-8 text-[#FF6B35]" />
            <div className="text-left">
              <p className="font-semibold text-gray-900">{selectedFile.name}</p>
              <p className="text-sm text-gray-500">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearFile();
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={disabled}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        ) : (
          <>
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-lg font-semibold text-gray-900 mb-1">
              Sleep je bestand hierheen
            </p>
            <p className="text-sm text-gray-600 mb-3">
              of klik om een bestand te selecteren
            </p>
            <p className="text-xs text-gray-500">
              CSV, TXT, Excel bestanden (max 5MB)
            </p>
          </>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
    </div>
  );
}
